import { extractSnapshotOptions, timeToMin, find0916Snapshot, extractTimePart } from '../../lib/snapshot-search';

export function classifyStrikes(
    rawOpts: any[],
    spotPrice: number,
    strikeGap: number,
    valKey: string,
    activeRange: number
): { itm: any[]; otm: any[] } {
    if (!rawOpts || rawOpts.length === 0 || spotPrice <= 0) {
        return { itm: [], otm: [] }
    }

    const atmStrike = Math.round(spotPrice / strikeGap) * strikeGap
    const minStrike = atmStrike - (activeRange * strikeGap)
    const maxStrike = atmStrike + (activeRange * strikeGap)

    const ceOptions = rawOpts.filter(o => (o.type === 'CE' || o.callOpenInterest !== undefined) && o.strike >= minStrike && o.strike <= maxStrike)
    const peOptions = rawOpts.filter(o => (o.type === 'PE' || o.putOpenInterest !== undefined) && o.strike >= minStrike && o.strike <= maxStrike)

    const mapVal = (o: any, t: 'CE'|'PE') => {
        if (o.type) return o[valKey] || 0
        if (valKey === 'oi') return t === 'CE' ? (o.callOpenInterest || 0) : (o.putOpenInterest || 0)
        if (valKey === 'volume') return t === 'CE' ? (o.callVolume || 0) : (o.putVolume || 0)
        if (valKey === 'oiChg') return t === 'CE' ? (o.callOpenInterestChange || 0) : (o.putOpenInterestChange || 0)
        if (valKey === 'combOI') return (o.callOpenInterest || 0) + (o.putOpenInterest || 0)
        if (valKey === 'combVol') return (o.callVolume || 0) + (o.putVolume || 0)
        return 0
    }

    const ceItm = ceOptions.filter(o => o.strike <= spotPrice).map(o => ({ ...o, type: 'CE', _val: mapVal(o, 'CE') }))
    const peItm = peOptions.filter(o => o.strike >= spotPrice).map(o => ({ ...o, type: 'PE', _val: mapVal(o, 'PE') }))

    const ceOtm = ceOptions.filter(o => o.strike > spotPrice).map(o => ({ ...o, type: 'CE', _val: mapVal(o, 'CE') }))
    const peOtm = peOptions.filter(o => o.strike < spotPrice).map(o => ({ ...o, type: 'PE', _val: mapVal(o, 'PE') }))

    ceItm.sort((a, b) => b._val - a._val)
    peItm.sort((a, b) => b._val - a._val)
    ceOtm.sort((a, b) => b._val - a._val)
    peOtm.sort((a, b) => b._val - a._val)

    return {
        itm: [...ceItm, ...peItm].sort((a, b) => b._val - a._val),
        otm: [...ceOtm, ...peOtm].sort((a, b) => b._val - a._val)
    }
}

export const cellDataKeys: Record<string, string> = {
    'Combined OI': 'combOI',
    'Combined Volume': 'combVol',
    'Top OI': 'oi',
    'Top Volume': 'volume',
    'Top Chng OI': 'oiChg',
};

export function ensureFlatOpts(rawOpts: any[]): any[] {
    if (!rawOpts || rawOpts.length === 0) return [];
    if (rawOpts[0].type) return rawOpts; // Already flat

    const flat: any[] = [];
    for (const opt of rawOpts) {
        const strike = opt.strike ?? opt.strikePrice ?? 0;
        const ceOI = opt.callOpenInterest ?? opt.oi ?? 0;
        const peOI = opt.putOpenInterest ?? opt.oi ?? 0;
        const ceVol = opt.callVolume ?? opt.volume ?? 0;
        const peVol = opt.putVolume ?? opt.volume ?? 0;
        const ceOiChg = opt.callOpenInterestChange ?? opt.oiChg ?? 0;
        const peOiChg = opt.putOpenInterestChange ?? opt.oiChg ?? 0;

        const ceLtp = opt.callLtp ?? opt.callPrice ?? opt.callClose ?? opt.ltp ?? opt.price ?? opt.close ?? opt.callOptionData?.ltp ?? opt.ce?.ltp ?? 0;
        const peLtp = opt.putLtp ?? opt.putPrice ?? opt.putClose ?? opt.ltp ?? opt.price ?? opt.close ?? opt.putOptionData?.ltp ?? opt.pe?.ltp ?? 0;

        flat.push({
            strike,
            type: 'CE',
            combOI: ceOI + peOI,
            combVol: ceVol + peVol,
            oi: ceOI,
            volume: ceVol,
            oiChg: ceOiChg,
            ltp: ceLtp,
        });
        flat.push({
            strike,
            type: 'PE',
            combOI: ceOI + peOI,
            combVol: ceVol + peVol,
            oi: peOI,
            volume: peVol,
            oiChg: peOiChg,
            ltp: peLtp,
        });
    }
    return flat;
}

export const getRowMin = (row: any): number => {
    if (row.timeVal !== undefined) return row.timeVal;
    const timeStr = row.time || row.timestampIST || row.timestamp || '';
    const tp = extractTimePart(timeStr);
    return tp ? timeToMin(tp) : 0;
};

export function buildProcessedHistory(baseHistory: any[], timeRangeEnd: string, isLiveShowing: boolean): any[] {
    if (!baseHistory || baseHistory.length === 0) return [];
    let sorted = [...baseHistory];
    sorted.sort((a: any, b: any) => {
        const tA = a.timeVal !== undefined ? a.timeVal : getRowMin(a);
        const tB = b.timeVal !== undefined ? b.timeVal : getRowMin(b);
        return tB - tA;
    });

    if (timeRangeEnd) {
        const maxMinutes = timeToMin(timeRangeEnd);
        sorted = sorted.filter((row: any) => {
            if (row.isLive) return isLiveShowing;
            const rowMin = row.timeVal !== undefined ? row.timeVal : getRowMin(row);
            return rowMin <= maxMinutes;
        });
    }
    return sorted;
}

const rowCalculationCache = new WeakMap<any, Record<string, any>>();

export function calculateTrendAnalysis(
    processedHistory: any[],
    symbol: string,
    expiry: string,
    strikeGap: number,
    activeRange: number,
    valKey: string
): any {
    if (!processedHistory || processedHistory.length === 0) return null;

    const historyWithITM = processedHistory.map((row: any) => {
        const cacheKey = `trend_${valKey}_${activeRange}_${strikeGap}`;
        let cachedData = rowCalculationCache.get(row);
        if (cachedData && cachedData[cacheKey]) {
            return cachedData[cacheKey];
        }

        const storedValKey = row._valKey || (row.isVolumeMode ? 'volume' : 'oi');
        if (row.itmTop5 && row.flatOpts && storedValKey === valKey) {
            // ALREADY CALCULATED legacy path
            return row;
        }
        
        let rawOpts = row.rawOpts;
        if (!rawOpts) {
            rawOpts = extractSnapshotOptions(row, symbol, expiry);
        }
        const flatOpts = ensureFlatOpts(rawOpts || []);
        
        const spotPrice = row.spotPrice || row.indexLtp || 0;
        const atmStrike = Math.round(spotPrice / strikeGap) * strikeGap;
        const { itm } = classifyStrikes(flatOpts, spotPrice, strikeGap, valKey, activeRange);
        
        const strikes = itm.map((o: any) => o.strike).filter(Boolean);
        const minS = strikes.length > 0 ? Math.min(...strikes) : null;
        const maxS = strikes.length > 0 ? Math.max(...strikes) : null;

        const minAllowedStrike = atmStrike - (activeRange * strikeGap);
        const maxAllowedStrike = atmStrike + (activeRange * strikeGap);
        
        const ceRows = flatOpts.filter((r: any) => r.type === 'CE' && r.strike >= minAllowedStrike && r.strike <= maxAllowedStrike);
        const peRows = flatOpts.filter((r: any) => r.type === 'PE' && r.strike >= minAllowedStrike && r.strike <= maxAllowedStrike);
        
        ceRows.sort((a: any, b: any) => (b[valKey] || 0) - (a[valKey] || 0));
        peRows.sort((a: any, b: any) => (b[valKey] || 0) - (a[valKey] || 0));
        
        const top3CE = ceRows.slice(0, 3).map((r: any) => r.strike);
        const top3PE = peRows.slice(0, 3).map((r: any) => r.strike);
        
        const resStrike = top3CE.length > 0 ? Math.min(...top3CE) : null;
        const supStrike = top3PE.length > 0 ? Math.max(...top3PE) : null;

        const timeVal = row.timeVal !== undefined ? row.timeVal : getRowMin(row);

        const result = {
            ...row,
            flatOpts,
            itmTop5: itm,
            minStrike: minS,
            maxStrike: maxS,
            resStrike,
            supStrike,
            timeVal,
            _valKey: valKey
        };
        
        if (!cachedData) cachedData = {};
        cachedData[cacheKey] = result;
        rowCalculationCache.set(row, cachedData);

        return result;
    });

    const liveRow = historyWithITM[0];
    if (!liveRow) return null;
    
    const liveITM = liveRow.itmTop5 || [];
    
    if (liveITM.length === 0) return null;

    let totalWeight = 0, weightedSum = 0, simpleSum = 0, count = 0;
    liveITM.forEach((opt: any, i: number) => {
        if (!opt) return;
        const weight = 5 - i;
        weightedSum += (opt.strike * weight);
        totalWeight += weight;
        simpleSum += opt.strike;
        count++;
    });

    const weightedAvg = totalWeight > 0 ? weightedSum / totalWeight : 0;
    const simpleAvg = count > 0 ? simpleSum / count : 0;
    const isBullishBase = weightedAvg > simpleAvg;
    const isBearishBase = weightedAvg < simpleAvg;

    const chronoHistory = [...historyWithITM].reverse();
    let priorAllDayMax = -Infinity, priorAllDayMin = Infinity;
    
    chronoHistory.forEach((row) => {
        if (row.timeVal === liveRow.timeVal) return;
        const strikes = (row.itmTop5 || []).map((o: any) => o.strike).filter(Boolean);
        if (strikes.length > 0) {
            const maxS = Math.max(...strikes);
            const minS = Math.min(...strikes);
            if (maxS > priorAllDayMax) priorAllDayMax = maxS;
            if (minS < priorAllDayMin) priorAllDayMin = minS;
        }
    });

    const liveMin = liveRow.minStrike, liveMax = liveRow.maxStrike;
    const liveRes = liveRow.resStrike, liveSup = liveRow.supStrike;

    let allDayMax = priorAllDayMax, allDayMin = priorAllDayMin;
    if (liveMin !== null && (allDayMin === Infinity || liveMin < allDayMin)) allDayMin = liveMin;
    if (liveMax !== null && (allDayMax === -Infinity || liveMax > allDayMax)) allDayMax = liveMax;

    let priorAllDayRes = -Infinity, priorAllDaySup = Infinity;
    for (let i = 1; i < historyWithITM.length; i++) {
        const row = historyWithITM[i];
        if (row.resStrike !== null && row.resStrike > priorAllDayRes) priorAllDayRes = row.resStrike;
        if (row.supStrike !== null && row.supStrike < priorAllDaySup) priorAllDaySup = row.supStrike;
    }

    let prevDifferentMin: number | null = null, prevDifferentMinRow: any = null;
    if (liveMin !== null) {
        for (let i = 1; i < historyWithITM.length; i++) {
            const row = historyWithITM[i];
            if (row.minStrike !== null && row.minStrike !== liveMin) {
                prevDifferentMin = row.minStrike; prevDifferentMinRow = row; break;
            }
        }
    }

    let prevDifferentMax: number | null = null, prevDifferentMaxRow: any = null;
    if (liveMax !== null) {
        for (let i = 1; i < historyWithITM.length; i++) {
            const row = historyWithITM[i];
            if (row.maxStrike !== null && row.maxStrike !== liveMax) {
                prevDifferentMax = row.maxStrike; prevDifferentMaxRow = row; break;
            }
        }
    }

    let prevDifferentRes: number | null = null;
    if (liveRes !== null) {
        for (let i = 1; i < historyWithITM.length; i++) {
            const row = historyWithITM[i];
            if (row.resStrike !== null && row.resStrike !== liveRes) {
                prevDifferentRes = row.resStrike; break;
            }
        }
    }

    let prevDifferentSup: number | null = null;
    if (liveSup !== null) {
        for (let i = 1; i < historyWithITM.length; i++) {
            const row = historyWithITM[i];
            if (row.supStrike !== null && row.supStrike !== liveSup) {
                prevDifferentSup = row.supStrike; break;
            }
        }
    }

    const minChange = (liveMin !== null && prevDifferentMin !== null) ? liveMin - prevDifferentMin : null;
    const maxChange = (liveMax !== null && prevDifferentMax !== null) ? liveMax - prevDifferentMax : null;

    const openRow = find0916Snapshot(historyWithITM);
    const openMinStrike = openRow ? openRow.minStrike : null;
    const openMaxStrike = openRow ? openRow.maxStrike : null;
    const openSupStrike = openRow ? openRow.supStrike : null;
    const openResStrike = openRow ? openRow.resStrike : null;

    let trendSignal = "Neutral / Sideways", signalTheme = "neutral", subText = "Strikes are range-bound", detailText = "No clear directional pressure from extreme strike shifts. Options activity is evenly distributed within the established range.";
    let highlightValue = "", targetStrike: number | null = null, icon = "Activity", isTrap = false, trapText = "", durationText = "", structuralIntegrityBadge: string | null = null;

    let oldSup: number | null = null, timePrevSup = 0;
    if (liveSup !== null) {
        for (let i = 1; i < historyWithITM.length; i++) {
            const row = historyWithITM[i];
            if (row.supStrike !== null && row.supStrike !== liveSup) {
                oldSup = row.supStrike; timePrevSup = row.timeVal || 0; break;
            }
        }
    }

    let oldRes: number | null = null, timePrevRes = 0;
    if (liveRes !== null) {
        for (let i = 1; i < historyWithITM.length; i++) {
            const row = historyWithITM[i];
            if (row.resStrike !== null && row.resStrike !== liveRes) {
                oldRes = row.resStrike; timePrevRes = row.timeVal || 0; break;
            }
        }
    }

    if (liveRes !== null && liveSup !== null) {
        const timeLive = liveRow.timeVal || 0;
        const minsSinceResChange = oldRes !== null ? timeLive - timePrevRes : Infinity;
        const minsSinceSupChange = oldSup !== null ? timeLive - timePrevSup : Infinity;

        const minJumpSize = prevDifferentMin !== null ? Math.abs(liveMin - prevDifferentMin) : 0;
        const maxJumpSize = prevDifferentMax !== null ? Math.abs(liveMax - prevDifferentMax) : 0;

        const timePrevMinRaw = prevDifferentMinRow ? (prevDifferentMinRow.timeVal || 0) : 0;
        const timePrevMaxRaw = prevDifferentMaxRow ? (prevDifferentMaxRow.timeVal || 0) : 0;
        const minsTakenMin = timePrevMinRaw > 0 ? timeLive - timePrevMinRaw : 0;
        const minsTakenMax = timePrevMaxRaw > 0 ? timeLive - timePrevMaxRaw : 0;

        const recentChangeTime = Math.min(minsSinceResChange, minsSinceSupChange);

        const atmStrike = Math.round((liveRow.spotPrice || liveRow.indexLtp || 0) / strikeGap) * strikeGap;
        const liveMaxRank = liveITM.findIndex((o: any) => o.strike === liveMax) + 1;
        const liveMinRank = liveITM.findIndex((o: any) => o.strike === liveMin) + 1;
        const gap = Math.abs(liveMaxRank - liveMinRank);
        const probPct = gap === 4 ? 90 : gap === 3 ? 75 : gap === 2 ? 60 : gap === 1 ? 50 : 0;
        
        let prevMaxRank = -1, prevMinRank = -1;
        if (historyWithITM[1] && historyWithITM[1].itmTop5) {
            prevMaxRank = historyWithITM[1].itmTop5.findIndex((o: any) => o.strike === historyWithITM[1].maxStrike) + 1;
            prevMinRank = historyWithITM[1].itmTop5.findIndex((o: any) => o.strike === historyWithITM[1].minStrike) + 1;
        }

        const isPreviouslyBullish = prevMaxRank > 0 && prevMinRank > 0 && prevMaxRank < prevMinRank;
        const isPreviouslyBearish = prevMaxRank > 0 && prevMinRank > 0 && prevMinRank < prevMaxRank;
        const isCurrentlyBullish = liveMaxRank > 0 && liveMinRank > 0 && liveMaxRank < liveMinRank;
        const isCurrentlyBearish = liveMaxRank > 0 && liveMinRank > 0 && liveMinRank < liveMaxRank;
        const isMaxBothCEPEInTop5 = liveITM.filter((o: any) => o.strike === liveMax).length === 2;
        const isMinBothCEPEInTop5 = liveITM.filter((o: any) => o.strike === liveMin).length === 2;
        const allTop5CE = liveITM.length > 0 && liveITM.every((o: any) => o.type === 'CE');
        const allTop5PE = liveITM.length > 0 && liveITM.every((o: any) => o.type === 'PE');

        if (allTop5CE) {
            trendSignal = "Bullish Reversal (Oversold)"; signalTheme = "bull"; subText = "CE Vol Spiking at Lows"; detailText = `Strong sudden spike in Call buying at lower ranks indicating a potential bottom and bullish reversal.`; highlightValue = "Reversal"; icon = "Zap";
        } else if (allTop5PE) {
            trendSignal = "Bearish Reversal (Overbought)"; signalTheme = "bear"; subText = "PE Vol Spiking at Highs"; detailText = `Strong sudden spike in Put buying at higher ranks indicating a potential top and bearish reversal.`; highlightValue = "Reversal"; icon = "Zap";
        } else if (liveMax === atmStrike && liveMaxRank === 1) {
            trendSignal = "Bullish Target Hit / Retracement"; signalTheme = "bull"; subText = "CE ITM Vol > PE ITM Vol"; detailText = `The Bullish target has been reached. We might see a retracement or temporary pause at these resistance levels.`; highlightValue = "Retracement"; targetStrike = 0; icon = "Target";
        } else if (isPreviouslyBullish && isCurrentlyBearish) {
            trendSignal = "Sharp Reversal (Bearish Cross)"; signalTheme = "bear"; subText = `MIN pushed MAX to Bottom Ranks`; detailText = `Trend was bullish (MAX in top ranks, MIN in bottom ranks) but suddenly MIN occupied TOP ranks and pushed MAX to Bottom ranks. We may see a sharp reversal.`; highlightValue = "Reversal"; icon = "ArrowDown";
        } else if (isPreviouslyBearish && isCurrentlyBullish) {
            trendSignal = "Sharp Reversal (Bullish Cross)"; signalTheme = "bull"; subText = `MAX pushed MIN to Bottom Ranks`; detailText = `Trend was bearish (MIN in top ranks, MAX in bottom ranks) but suddenly MAX occupied TOP ranks and pushed MIN to Bottom ranks. We may see a sharp reversal.`; highlightValue = "Reversal"; icon = "ArrowUp";
        } else if (liveMax === atmStrike && liveMaxRank > 1 && isMaxBothCEPEInTop5) {
            trendSignal = "Wait for Clear Trend"; signalTheme = "neutral"; subText = `MAX Strike sitting both weightage`; detailText = `MAX is ATM and both CE/PE strikes are sitting in Top 5 ranks. One may be in bottom ranks and another in top ranks which leads to confusion. Wait for clear trend.`; highlightValue = "Wait"; icon = "Target";
        } else if (liveMin === atmStrike && liveMinRank > 1 && isMinBothCEPEInTop5) {
            trendSignal = "Wait for Clear Trend"; signalTheme = "neutral"; subText = `MIN Strike sitting both weightage`; detailText = `MIN is ATM and both CE/PE strikes are sitting in Top 5 ranks. One may be in bottom ranks and another in top ranks which leads to confusion. Wait for clear trend.`; highlightValue = "Wait"; icon = "Target";
        } else if (isCurrentlyBullish) {
            trendSignal = "Bullish Trend Confirmation"; signalTheme = "bull"; subText = "MAX is Top Rank & Near ATM"; detailText = `The MAX strike is showing very high activity near ATM levels, confirming a strong underlying bullish trend.`; highlightValue = "Bullish"; targetStrike = liveMax || 0; icon = "TrendingUp";
        } else if (isCurrentlyBearish) {
            trendSignal = "Bearish Trend Confirmation"; signalTheme = "bear"; subText = "MIN is Top Rank & Near ATM"; detailText = `The MIN strike is showing very high activity near ATM levels, confirming a strong underlying bearish trend.`; highlightValue = "Bearish"; targetStrike = liveMin || 0; icon = "TrendingDown";
        }

        let supportIntegrity: 'ironclad' | 'weak' | null = null;
        let resistanceIntegrity: 'ironclad' | 'weak' | null = null;
        let maxCallVol = -1, maxPutVol = -1;
        
        const liveFlatOpts = liveRow.flatOpts || [];
        liveFlatOpts.forEach((opt: any) => {
            if (opt.type === 'CE' && opt.volume > maxCallVol) maxCallVol = opt.volume;
            if (opt.type === 'PE' && opt.volume > maxPutVol) maxPutVol = opt.volume;
        });

        const liveSupPeOpt = liveFlatOpts.find((o: any) => o.strike === liveSup && o.type === 'PE');
        if (liveSupPeOpt && maxPutVol > 0 && liveSupPeOpt.volume >= maxPutVol * 0.7) supportIntegrity = liveSupPeOpt.oiChg > 0 ? 'ironclad' : 'weak';

        const liveResCeOpt = liveFlatOpts.find((o: any) => o.strike === liveRes && o.type === 'CE');
        if (liveResCeOpt && maxCallVol > 0 && liveResCeOpt.volume >= maxCallVol * 0.7) resistanceIntegrity = liveResCeOpt.oiChg > 0 ? 'ironclad' : 'weak';

        if (signalTheme === 'bull' && supportIntegrity) structuralIntegrityBadge = supportIntegrity === 'ironclad' ? 'Ironclad Support' : 'Weak Support';
        else if (signalTheme === 'bear' && resistanceIntegrity) structuralIntegrityBadge = resistanceIntegrity === 'ironclad' ? 'Ironclad Resistance' : 'Weak Resistance';
        
        if (recentChangeTime === Infinity) durationText = `Stable All Day`;
        else if (recentChangeTime === 0) durationText = `🚨 Just Triggered (0 mins)`;
        else durationText = `Confirmed for ${Math.floor(recentChangeTime)} mins`;

        if (signalTheme === 'bull' && isBearishBase) {
            isTrap = true; trapText = "SEVERE BULL TRAP DETECTED"; detailText = `⚠️ DANGER: Temporary bullish shift occurred, but the internal base structure is heavily BEARISH. This is likely a fake breakout designed to trap call buyers.`;
        } else if (signalTheme === 'bear' && isBullishBase) {
            isTrap = true; trapText = "SEVERE BEAR TRAP DETECTED"; detailText = `⚠️ DANGER: Temporary bearish shift occurred, but the internal base structure is heavily BULLISH. This is likely a fake breakdown designed to trap put buyers.`;
        } else if (signalTheme === 'bull' && openResStrike !== null && liveRes < openResStrike) {
            isTrap = true; trapText = "Bull Trap Warning: Below Open Resistance";
        } else if (signalTheme === 'bear' && openSupStrike !== null && liveSup > openSupStrike) {
            isTrap = true; trapText = "Bear Trap Warning: Above Open Support";
        }
    }

    return {
        weightedAvg, simpleAvg, isBullishBase, isBearishBase,
        liveMax, liveMin, liveRes, liveSup,
        prevDifferentMin, prevDifferentMax, minChange, maxChange,
        openMinStrike, openMaxStrike, allDayMax: allDayMax === -Infinity ? null : allDayMax, allDayMin: allDayMin === Infinity ? null : allDayMin,
        liveITMStrikes: liveITM.map((r: any) => r.strike).filter(Boolean),
        trendSignal, signalTheme, subText, detailText, highlightValue, structuralIntegrityBadge, targetStrike, isTrap, trapText, durationText, Icon: icon,
        data: historyWithITM
    };
}

export function calculateMarketActivityTrend(
    historyWithITM: any[],
    activeRange: number,
    strikeGap: number,
    valKey: string,
    topN: number = 10
): 'Bullish' | 'Bearish' | 'Neutral' {
    if (!historyWithITM || historyWithITM.length < 2) return 'Neutral';

    const tableData = historyWithITM.map(row => {
        const rawOpts = row.flatOpts || row.rawOpts || [];
        const spotPrice = row.spotPrice || 0;
        const atmStrike = Math.round(spotPrice / strikeGap) * strikeGap;
        const minStrike = atmStrike - (activeRange * strikeGap);
        const maxStrike = atmStrike + (activeRange * strikeGap);
        
        const inRange = rawOpts.filter((opt: any) => opt.strike >= minStrike && opt.strike <= maxStrike);
        const itmOpts = inRange.filter((opt: any) => {
            if (opt.strike === atmStrike) return true;
            return opt.type === 'CE' ? opt.strike < spotPrice : opt.strike > spotPrice;
        });
        
        const uniqueItmOpts: any[] = [];
        const seen = new Set<string>();
        for (const opt of itmOpts) {
            const key = `${opt.strike}_${opt.type}`;
            if (!seen.has(key)) {
                seen.add(key);
                uniqueItmOpts.push(opt);
            }
        }
        uniqueItmOpts.sort((a, b) => (Number(b[valKey]) || 0) - (Number(a[valKey]) || 0));
        return { ...row, itmStrikes: uniqueItmOpts.slice(0, topN) };
    });

    const getValidStrikes = (row: any) => {
        const rawOpts = row.flatOpts || row.rawOpts || [];
        const spotPrice = row.spotPrice || 0;
        const atm = Math.round(spotPrice / strikeGap) * strikeGap;
        const min = atm - (activeRange * strikeGap);
        const max = atm + (activeRange * strikeGap);
        const inRange = rawOpts.filter((opt: any) => opt.strike >= min && opt.strike <= max);
        return {
            validCE: new Set<number>(inRange.filter((o: any) => o.type === 'CE').map((o: any) => o.strike)),
            validPE: new Set<number>(inRange.filter((o: any) => o.type === 'PE').map((o: any) => o.strike))
        };
    };

    const rawRows = [];
    for (let i = 0; i < tableData.length - 1; i++) {
        const current = tableData[i]!;
        const previous = tableData[i + 1]!;
        const currCE = new Set<number>(current.itmStrikes.filter((o: any) => o?.type === 'CE').map((o: any) => o.strike));
        const currPE = new Set<number>(current.itmStrikes.filter((o: any) => o?.type === 'PE').map((o: any) => o.strike));
        const prevCE = new Set<number>(previous.itmStrikes.filter((o: any) => o?.type === 'CE').map((o: any) => o.strike));
        const prevPE = new Set<number>(previous.itmStrikes.filter((o: any) => o?.type === 'PE').map((o: any) => o.strike));
        
        const { validCE, validPE } = getValidStrikes(current);
        const droppedCE = Array.from(prevCE).filter(s => !currCE.has(s) && validCE.has(s)).sort((a, b) => b - a);
        const droppedPE = Array.from(prevPE).filter(s => !currPE.has(s) && validPE.has(s)).sort((a, b) => b - a);
        const addedCE = Array.from(currCE).filter(s => !prevCE.has(s)).sort((a, b) => b - a);
        const addedPE = Array.from(currPE).filter(s => !prevPE.has(s)).sort((a, b) => b - a);
        
        if (droppedCE.length > 0 || droppedPE.length > 0 || addedCE.length > 0 || addedPE.length > 0) {
            rawRows.push({ droppedCE, droppedPE, addedCE, addedPE });
        }
    }

    let lastDroppedCE: number | null = null;
    let lastDroppedPE: number | null = null;
    let lastAddedCE: number | null = null;
    let lastAddedPE: number | null = null;
    let currentSignal: 'Bullish' | 'Bearish' | 'Neutral' = 'Neutral';

    const processStrikes = (strikes: number[], lastStrike: number | null) => {
        const res: {strike: number, trend: 'up'|'down'|'none'}[] = [];
        for (const strike of strikes) {
            let trend: 'up' | 'down' | 'none' = 'none';
            if (lastStrike !== null) {
                if (strike > lastStrike) trend = 'up';
                else if (strike < lastStrike) trend = 'down';
            }
            if (lastStrike !== null && trend === 'none') continue;
            res.push({ strike, trend });
        }
        const newLastStrike = strikes.length > 0 ? strikes[strikes.length - 1]! : lastStrike;
        return { res, newLastStrike };
    };

    for (let i = rawRows.length - 1; i >= 0; i--) {
        const row = rawRows[i]!;
        
        const dCE = processStrikes(row.droppedCE, lastDroppedCE);
        lastDroppedCE = dCE.newLastStrike;
        const dPE = processStrikes(row.droppedPE, lastDroppedPE);
        lastDroppedPE = dPE.newLastStrike;
        
        const aCE = processStrikes(row.addedCE, lastAddedCE);
        lastAddedCE = aCE.newLastStrike;
        const aPE = processStrikes(row.addedPE, lastAddedPE);
        lastAddedPE = aPE.newLastStrike;

        let rowSignal: 'Bullish' | 'Bearish' | 'Neutral' = currentSignal;
        if (aCE.res.length > 0) {
            if (aCE.res[0]!.trend === 'up') rowSignal = 'Bullish';
            else if (aCE.res[0]!.trend === 'down') rowSignal = 'Bearish';
        } else if (aPE.res.length > 0) {
            if (aPE.res[0]!.trend === 'up') rowSignal = 'Bullish';
            else if (aPE.res[0]!.trend === 'down') rowSignal = 'Bearish';
        }
        currentSignal = rowSignal;
    }

    return currentSignal;
}
